import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLConnection } from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { CompanyRecentActivity } from './useCompanyRecentActivitiesGraphQL';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL ---

interface ActivityNode {
  activityId: number;
  customerId: number;
  activityType: string;
  title: string;
  description: string;
  actorName: string;
  createdAt: string;
}

interface ActivityResponse {
  customers: {
    customerRecentActivityList: GraphQLConnection<ActivityNode>;
  };
}

const CUSTOMER_RECENT_ACTIVITY_QUERY = `
  query CustomerRecentActivity($first: Int, $filter: CustomerRecentActivityListFilterInput, $sort: [CustomerRecentActivityListSortSortByInput!]) {
    customers {
      customerRecentActivityList(first: $first, filter: $filter, sort: $sort) {
        edges {
          node {
            activityId
            customerId
            activityType
            title
            description
            actorName
            createdAt
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Mock data ---

function buildMockActivities(): CompanyRecentActivity[] {
  return [
    {
      id: 'mock-1',
      activityType: 'email',
      title: 'Payment reminder sent',
      description: 'Reminder for overdue invoices',
      customerName: 'Customer',
      actorName: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    },
    {
      id: 'mock-2',
      activityType: 'phone',
      title: 'Follow-up call completed',
      description: 'Discussed payment schedule',
      customerName: 'Customer',
      actorName: 'Mike Chen',
      createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    },
    {
      id: 'mock-3',
      activityType: 'payment',
      title: 'Payment received',
      description: 'Partial payment allocated',
      customerName: 'Customer',
      actorName: 'System',
      createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    },
    {
      id: 'mock-4',
      activityType: 'note',
      title: 'Note added',
      description: 'Customer requested payment plan',
      customerName: 'Customer',
      actorName: 'Emily Rodriguez',
      createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    },
    {
      id: 'mock-5',
      activityType: 'status_change',
      title: 'Case status changed',
      description: 'Escalated to senior collector',
      customerName: 'Customer',
      actorName: 'System',
      createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    },
  ];
}

// --- Hook ---

export interface CustomerRecentActivitiesResult {
  activities: CompanyRecentActivity[];
  isLoading: boolean;
}

export function useCustomerRecentActivities(customerId: number | undefined): CustomerRecentActivitiesResult {
  const apiConfig = useApiConfig();
  const mockData = buildMockActivities();

  const { data, isLoading } = useModeAwareQuery<CompanyRecentActivity[], Error>(
    ['customer-recent-activities', customerId],
    async ({ signal }) => {
      if (customerId === undefined) return [];

      const response = await graphqlQuery<ActivityResponse>(
        apiConfig,
        CUSTOMER_RECENT_ACTIVITY_QUERY,
        {
          first: 10,
          filter: { customerId: { eq: customerId } },
          sort: [{ field: 'CreatedAt', order: 'Descending' }],
        },
        signal,
      );

      return response.customers.customerRecentActivityList.edges.map((e) => ({
        id: String(e.node.activityId),
        activityType: e.node.activityType,
        title: e.node.title,
        description: e.node.description,
        customerName: e.node.actorName,
        actorName: e.node.actorName,
        createdAt: e.node.createdAt,
      }));
    },
    mockData,
    {
      enabled: customerId !== undefined,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  return { activities: data ?? [], isLoading };
}
